import { useEffect, useRef, useState, useMemo, useCallback } from "react";
import api, { formatApiError, API } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Upload, Download, Trash2, FileStack, ShieldAlert, RefreshCw, Search, AlertTriangle } from "lucide-react";

const STATUS_TONE = {
  match: "text-emerald-700 border-emerald-200 bg-emerald-50",
  over: "text-red-700 border-red-200 bg-red-50",
  under: "text-amber-700 border-amber-200 bg-amber-50",
  missing_dd: "text-blue-700 border-blue-200 bg-blue-50",
  missing_payroll: "text-purple-700 border-purple-200 bg-purple-50",
  below_floor: "text-orange-700 border-orange-200 bg-orange-50",
};
const STATUS_LABEL = {
  match: "Match",
  over: "Over",
  under: "Under",
  missing_dd: "Missing DD",
  missing_payroll: "Missing payroll",
  below_floor: "Below €floor",
};

const fmtEur = (n) => new Intl.NumberFormat("en-IE", { style: "currency", currency: "EUR" }).format(Number(n) || 0);
const fmtSigned = (n) => {
  const v = Number(n) || 0;
  if (Math.abs(v) < 0.01) return fmtEur(0);
  return (v > 0 ? "+" : "") + fmtEur(v);
};

const months = [
  { v: "01", label: "Jan" }, { v: "02", label: "Feb" }, { v: "03", label: "Mar" },
  { v: "04", label: "Apr" }, { v: "05", label: "May" }, { v: "06", label: "Jun" },
  { v: "07", label: "Jul" }, { v: "08", label: "Aug" }, { v: "09", label: "Sep" },
  { v: "10", label: "Oct" }, { v: "11", label: "Nov" }, { v: "12", label: "Dec" },
];
const yearsRange = (from = 2022, to = 2030) => {
  const out = []; for (let y = from; y <= to; y++) out.push(String(y)); return out;
};

export default function InsurancePage() {
  const [summary, setSummary] = useState(null);
  const [files, setFiles] = useState([]);
  const [kind, setKind] = useState("auto");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const fileRef = useRef();

  // Recon filters
  const today = new Date();
  const [yearN, setYearN] = useState(String(today.getFullYear()));
  const [monthN, setMonthN] = useState(String(today.getMonth() + 1).padStart(2, "0"));
  const [employee, setEmployee] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");

  const [recon, setRecon] = useState(null);
  const [reconLoading, setReconLoading] = useState(false);
  const [policy, setPolicy] = useState({ employer_rate: 0.015, combined_floor: 100.0, tolerance_eur: 1.0 });
  const [policyForm, setPolicyForm] = useState({ employer_rate: 1.5, combined_floor: 100.0, tolerance_eur: 1.0 });

  const load = useCallback(async () => {
    try {
      const [s, f, st] = await Promise.all([
        api.get("/insurance/summary"),
        api.get("/insurance/files"),
        api.get("/settings"),
      ]);
      setSummary(s.data);
      setFiles(f.data);
      const pol = st.data?.insurance || { employer_rate: 0.015, combined_floor: 100.0, tolerance_eur: 1.0 };
      setPolicy(pol);
      setPolicyForm({
        employer_rate: Number(((pol.employer_rate ?? 0.015) * 100).toFixed(2)),
        combined_floor: Number(pol.combined_floor ?? 100.0),
        tolerance_eur: Number(pol.tolerance_eur ?? 1.0),
      });
    } catch (e) {
      toast.error(formatApiError(e));
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const upload = async () => {
    const file = fileRef.current?.files?.[0];
    if (!file) return toast.error("Pick a file first");
    setBusy(true);
    try {
      const form = new FormData();
      form.append("file", file);
      form.append("kind", kind);
      if (note) form.append("note", note);
      const res = await api.post("/insurance/upload", form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const d = res.data;
      if (d.parse_error) toast.error(`Parse error: ${d.parse_error}`);
      else if (d.inserted_entries === 0 && d.skipped_duplicates > 0) toast.info(`No new rows — ${d.skipped_duplicates} duplicates skipped`);
      else toast.success(`${d.detected_kind === "payroll" ? "Payroll" : "Direct debit"} parsed — ${d.inserted_entries} new rows${d.skipped_duplicates ? ` (${d.skipped_duplicates} duplicates skipped)` : ""}`);
      setNote("");
      if (fileRef.current) fileRef.current.value = "";
      await load();
      await runRecon();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const removeFile = async (id) => {
    if (!window.confirm("Delete this file and its parsed entries?")) return;
    try {
      await api.delete(`/insurance/files/${id}`);
      toast.success("File removed");
      await load();
      await runRecon();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const downloadFile = async (id, filename) => {
    try {
      const url = `${API}/insurance/files/${id}/download`;
      const a = document.createElement("a");
      a.href = url; a.download = filename || "insurance-file"; a.target = "_blank";
      document.body.appendChild(a); a.click(); a.remove();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const runRecon = useCallback(async () => {
    setReconLoading(true);
    try {
      const params = {};
      if (yearN !== "any" && monthN !== "any") {
        params.month_from = `${yearN}-${monthN}`;
        params.month_to = `${yearN}-${monthN}`;
      } else if (yearN !== "any") {
        params.month_from = `${yearN}-01`;
        params.month_to = `${yearN}-12`;
      }
      if (employee) params.employee = employee;
      if (statusFilter && statusFilter !== "all") params.status_filter = statusFilter;
      const r = await api.get("/insurance/reconciliation", { params });
      setRecon(r.data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setReconLoading(false);
    }
  }, [yearN, monthN, employee, statusFilter]);

  useEffect(() => { runRecon(); }, [runRecon]);

  const savePolicy = async () => {
    try {
      const body = {
        employer_rate: Number(policyForm.employer_rate) / 100, // % → fraction
        combined_floor: Number(policyForm.combined_floor),
        tolerance_eur: Number(policyForm.tolerance_eur),
        dd_attribution: policy.dd_attribution || "month_of_deduction",
        dd_day_of_month: policy.dd_day_of_month || 6,
      };
      await api.put("/settings/insurance", body);
      toast.success("Insurance policy saved");
      await load();
      await runRecon();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const rows = recon?.rows || [];
  const summaryBlock = recon?.summary;
  const underFloor = summaryBlock?.employees_under_floor || [];

  const statusCounts = useMemo(() => {
    const c = { match: 0, over: 0, under: 0, missing_dd: 0, missing_payroll: 0, below_floor: 0 };
    for (const r of rows) c[r.status] = (c[r.status] || 0) + 1;
    return c;
  }, [rows]);

  return (
    <div className="space-y-6" data-testid="insurance-page">
      <div>
        <div className="tiny-label">Admin only</div>
        <h1 className="font-display text-3xl sm:text-4xl font-bold tracking-tight">Insurance reconciliation</h1>
        <p className="text-sm text-muted-foreground mt-1 max-w-3xl">
          Upload weekly Payroll Summary files (.xls / .xlsx) and the Zurich direct-debit CSV. The system aggregates monthly gross pay,
          calculates employer 1.5% + employee top-up to a €100 combined floor, and reconciles against the actual DD amounts.
        </p>
      </div>

      {/* Policy card */}
      <Card className="border-border shadow-none">
        <CardContent className="p-4 grid sm:grid-cols-4 gap-3 items-end">
          <div>
            <Label className="text-xs">Employer rate (% of gross)</Label>
            <Input
              type="number" step="0.1" min="0" max="100"
              value={policyForm.employer_rate}
              onChange={(e) => setPolicyForm({ ...policyForm, employer_rate: e.target.value })}
              className="h-9" data-testid="ins-rate"
            />
          </div>
          <div>
            <Label className="text-xs">Combined floor (€/employee/month)</Label>
            <Input
              type="number" step="1" min="0"
              value={policyForm.combined_floor}
              onChange={(e) => setPolicyForm({ ...policyForm, combined_floor: e.target.value })}
              className="h-9" data-testid="ins-floor"
            />
          </div>
          <div>
            <Label className="text-xs">Tolerance (€)</Label>
            <Input
              type="number" step="0.01" min="0"
              value={policyForm.tolerance_eur}
              onChange={(e) => setPolicyForm({ ...policyForm, tolerance_eur: e.target.value })}
              className="h-9" data-testid="ins-tol"
            />
          </div>
          <Button onClick={savePolicy} className="h-9" data-testid="ins-save-policy">Save policy</Button>
        </CardContent>
      </Card>

      {/* Summary alert */}
      {summaryBlock && (
        <Alert className={underFloor.length > 0 ? "border-orange-300 bg-orange-50" : ""} data-testid="ins-alert">
          <ShieldAlert className="w-4 h-4" />
          <AlertTitle>
            {underFloor.length > 0
              ? `${underFloor.length} employee${underFloor.length === 1 ? "" : "s"} below €${policy.combined_floor || 100} combined floor`
              : "Combined floor satisfied"}
          </AlertTitle>
          <AlertDescription className="space-y-1 mt-1">
            <div>
              Employer required <b>{fmtEur(summaryBlock.total_required_employer)}</b> · paid <b>{fmtEur(summaryBlock.total_paid_employer)}</b> ·
              variance <b className={summaryBlock.variance_employer > 0.01 ? "text-amber-700" : (summaryBlock.variance_employer < -0.01 ? "text-red-700" : "")}>
                {fmtSigned(summaryBlock.variance_employer)}
              </b>
            </div>
            <div>
              Combined required <b>{fmtEur(summaryBlock.total_required)}</b> · paid <b>{fmtEur(summaryBlock.total_paid)}</b> ·
              variance <b>{fmtSigned(summaryBlock.variance_total)}</b>
            </div>
            {underFloor.length > 0 && (
              <div className="text-xs text-orange-700 mt-1">
                Below floor: {underFloor.slice(0, 6).join(", ")}{underFloor.length > 6 ? ` and ${underFloor.length - 6} more…` : ""}
              </div>
            )}
          </AlertDescription>
        </Alert>
      )}

      {/* Counts */}
      <div className="flex flex-wrap gap-2 text-xs">
        <Badge variant="outline" className={STATUS_TONE.match}>Match {statusCounts.match}</Badge>
        <Badge variant="outline" className={STATUS_TONE.under}>Under {statusCounts.under}</Badge>
        <Badge variant="outline" className={STATUS_TONE.over}>Over {statusCounts.over}</Badge>
        <Badge variant="outline" className={STATUS_TONE.below_floor}>Below floor {statusCounts.below_floor}</Badge>
        <Badge variant="outline" className={STATUS_TONE.missing_dd}>Missing DD {statusCounts.missing_dd}</Badge>
        <Badge variant="outline" className={STATUS_TONE.missing_payroll}>Missing payroll {statusCounts.missing_payroll}</Badge>
      </div>

      {/* Upload card */}
      <Card className="border-border shadow-none">
        <CardContent className="p-4 grid sm:grid-cols-12 gap-3 items-end">
          <div className="sm:col-span-3">
            <Label className="text-xs">File type</Label>
            <Select value={kind} onValueChange={setKind}>
              <SelectTrigger className="h-9" data-testid="ins-kind"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="auto">Auto-detect</SelectItem>
                <SelectItem value="payroll">Payroll Summary (.xls / .xlsx)</SelectItem>
                <SelectItem value="insurance_dd">Zurich DD (.csv)</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="sm:col-span-5">
            <Label className="text-xs">File</Label>
            <input ref={fileRef} type="file" className="block w-full text-sm" accept=".xls,.xlsx,.csv,.txt" data-testid="ins-file-input" />
          </div>
          <div className="sm:col-span-3">
            <Label className="text-xs">Note (optional)</Label>
            <Input value={note} onChange={(e) => setNote(e.target.value)} placeholder="e.g. Week 18 2026" className="h-9" data-testid="ins-note" />
          </div>
          <div className="sm:col-span-1">
            <Button onClick={upload} disabled={busy} className="h-9 w-full" data-testid="ins-upload">
              <Upload className="w-4 h-4 mr-1.5" /> {busy ? "..." : "Upload"}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Files list */}
      {files.length > 0 && (
        <Card className="border-border shadow-none">
          <CardContent className="p-0">
            <div className="px-4 py-2 bg-muted/40 border-b border-border text-xs font-semibold tracking-wide text-muted-foreground flex items-center justify-between">
              <span><FileStack className="w-3.5 h-3.5 inline-block mr-1.5" /> Uploaded files</span>
              <span>{files.length}</span>
            </div>
            <div className="divide-y divide-border">
              {files.map((f) => (
                <div key={f.id} className="px-4 py-2.5 flex items-center gap-3 text-sm" data-testid={`ins-file-${f.id}`}>
                  <Badge variant="outline" className={f.kind === "payroll" ? "text-blue-700 border-blue-200 bg-blue-50" : "text-violet-700 border-violet-200 bg-violet-50"}>
                    {f.kind === "payroll" ? "Payroll" : "DD"}
                  </Badge>
                  <span className="font-medium truncate">{f.filename}</span>
                  <span className="text-xs text-muted-foreground">{f.parsed_rows ?? 0} rows{f.week_number ? ` · Wk ${f.week_number}` : ""}</span>
                  {f.parse_error && <Badge variant="outline" className="text-red-700 border-red-200 bg-red-50">parse error</Badge>}
                  <span className="text-xs text-muted-foreground ml-auto">{f.uploaded_at?.replace("T", " ")?.slice(0, 16)}</span>
                  <Button size="icon" variant="ghost" onClick={() => downloadFile(f.id, f.filename)} title="Download">
                    <Download className="w-4 h-4" />
                  </Button>
                  <Button size="icon" variant="ghost" onClick={() => removeFile(f.id)} title="Delete">
                    <Trash2 className="w-4 h-4 text-red-600" />
                  </Button>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Recon filters */}
      <Card className="border-border shadow-none">
        <CardContent className="p-4 flex flex-wrap items-end gap-3">
          <div>
            <Label className="text-xs">Year</Label>
            <Select value={yearN} onValueChange={setYearN}>
              <SelectTrigger className="h-9 w-28" data-testid="ins-year"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="any">Any</SelectItem>
                {yearsRange().map((y) => <SelectItem key={y} value={y}>{y}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs">Month</Label>
            <Select value={monthN} onValueChange={setMonthN}>
              <SelectTrigger className="h-9 w-28" data-testid="ins-month"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="any">Any</SelectItem>
                {months.map((m) => <SelectItem key={m.v} value={m.v}>{m.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="min-w-[200px] flex-1">
            <Label className="text-xs">Employee</Label>
            <div className="relative">
              <Search className="w-4 h-4 absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none" />
              <Input value={employee} onChange={(e) => setEmployee(e.target.value)} placeholder="Name contains…" className="pl-8 h-9" data-testid="ins-emp" />
            </div>
          </div>
          <div>
            <Label className="text-xs">Status</Label>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="h-9 w-44" data-testid="ins-status"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All</SelectItem>
                <SelectItem value="discrepancy">Discrepancies only</SelectItem>
                <SelectItem value="match">Match</SelectItem>
                <SelectItem value="under">Under</SelectItem>
                <SelectItem value="over">Over</SelectItem>
                <SelectItem value="below_floor">Below floor</SelectItem>
                <SelectItem value="missing_dd">Missing DD</SelectItem>
                <SelectItem value="missing_payroll">Missing payroll</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <Button variant="outline" className="h-9" onClick={runRecon} disabled={reconLoading} data-testid="ins-refresh">
            <RefreshCw className={`w-4 h-4 mr-1.5 ${reconLoading ? "animate-spin" : ""}`} /> Refresh
          </Button>
        </CardContent>
      </Card>

      {/* Recon table */}
      <Card className="border-border shadow-none">
        <CardContent className="p-0 overflow-x-auto">
          {reconLoading ? (
            <div className="p-8 text-center text-muted-foreground text-sm">Loading…</div>
          ) : rows.length === 0 ? (
            <div className="p-10 text-center text-muted-foreground text-sm">
              {summary?.payroll_entries === 0 ? "Upload a Payroll Summary file to begin." : "No rows match the current filters."}
            </div>
          ) : (
            <table className="w-full text-sm" data-testid="ins-table">
              <thead className="text-xs uppercase tracking-wide text-muted-foreground border-b border-border bg-muted/30">
                <tr>
                  <th className="px-3 py-2 text-left">Employee</th>
                  <th className="px-3 py-2 text-left">Month</th>
                  <th className="px-3 py-2 text-right">Gross</th>
                  <th className="px-3 py-2 text-right">Req ER</th>
                  <th className="px-3 py-2 text-right">Req EE</th>
                  <th className="px-3 py-2 text-right">Req Total</th>
                  <th className="px-3 py-2 text-right">DD ER</th>
                  <th className="px-3 py-2 text-right">DD EE</th>
                  <th className="px-3 py-2 text-right">DD Total</th>
                  <th className="px-3 py-2 text-right">Var Total</th>
                  <th className="px-3 py-2 text-left">Status</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={`${r.employee_name_canonical}|${r.month}`} className="border-b border-border last:border-b-0 hover:bg-muted/20" data-testid={`ins-row-${r.employee_name_canonical}`}>
                    <td className="px-3 py-2 font-medium">{r.employee_name}</td>
                    <td className="px-3 py-2 text-muted-foreground">{r.month}</td>
                    <td className="px-3 py-2 text-right font-mono">{fmtEur(r.gross_pay)}</td>
                    <td className="px-3 py-2 text-right font-mono">{fmtEur(r.required_employer)}</td>
                    <td className="px-3 py-2 text-right font-mono">{fmtEur(r.required_employee)}</td>
                    <td className="px-3 py-2 text-right font-mono font-medium">{fmtEur(r.required_total)}</td>
                    <td className="px-3 py-2 text-right font-mono text-muted-foreground">{fmtEur(r.dd_employer)}</td>
                    <td className="px-3 py-2 text-right font-mono text-muted-foreground">{fmtEur(r.dd_employee)}</td>
                    <td className="px-3 py-2 text-right font-mono">{fmtEur(r.dd_total)}</td>
                    <td className={`px-3 py-2 text-right font-mono ${Math.abs(r.variance_total) > (recon?.policy?.tolerance_eur || 1) ? "font-semibold" : ""}`}>
                      {fmtSigned(r.variance_total)}
                    </td>
                    <td className="px-3 py-2">
                      <Badge variant="outline" className={`${STATUS_TONE[r.status] || ""}`}>
                        {r.below_floor && r.status !== "below_floor" && <AlertTriangle className="w-3 h-3 mr-1" />}
                        {STATUS_LABEL[r.status] || r.status}
                      </Badge>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
